import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { getErrorMessage } from "@/lib/errorMessage";
import { ShieldCheck, ShieldOff, Loader2, Copy, Check } from "lucide-react";

/**
 * MFA (TOTP) settings dialog — Feb 2026.
 *
 * Three-state UI:
 *   1. **Idle** — probe /auth/mfa/status; if enrolled, show
 *      "MFA is enabled" + Disable button + remaining backup-code
 *      count. If not enrolled, show "Set up MFA" button.
 *   2. **Enrolling** — after /enroll/start: show QR + manual key +
 *      code input. On successful confirm, jump to state 3.
 *   3. **Backup codes** — one-shot display of the 10 codes returned
 *      by /enroll/confirm. Explicit "I've saved these codes" button
 *      dismisses back to Idle.
 */
export function MfaSettingsDialog({ open, onOpenChange }) {
  const [phase, setPhase] = useState("idle"); // idle | enrolling | backup | disabling
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enrollData, setEnrollData] = useState(null); // { secret, otpauth_url, qr_png_data_url, backup_codes }
  const [code, setCode] = useState("");
  const [savedBackup, setSavedBackup] = useState(false);
  const [finalCodes, setFinalCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const r = await api.get("/auth/mfa/status");
      setStatus(r.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not load MFA status"));
    }
  }, []);

  useEffect(() => {
    if (open) {
      setPhase("idle");
      setEnrollData(null);
      setFinalCodes(null);
      setCode("");
      setSavedBackup(false);
      setCopied(false);
      refreshStatus();
    }
  }, [open, refreshStatus]);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/enroll/start");
      setEnrollData(r.data);
      setPhase("enrolling");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not start enrollment"));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/enroll/confirm", { code });
      setFinalCodes(r.data.backup_codes || []);
      setPhase("backup");
      setCode("");
      toast.success("MFA enabled");
    } catch (err) {
      toast.error(getErrorMessage(err, "Code did not verify"));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code });
      toast.success("MFA disabled");
      setPhase("idle");
      setCode("");
      await refreshStatus();
    } catch (err) {
      toast.error(getErrorMessage(err, "Code did not verify"));
    } finally {
      setBusy(false);
    }
  };

  const copyBackup = () => {
    if (!finalCodes) return;
    navigator.clipboard.writeText(finalCodes.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => toast.error("Copy failed"));
  };

  const dismissBackup = async () => {
    setPhase("idle");
    setFinalCodes(null);
    setSavedBackup(false);
    await refreshStatus();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mfa-settings-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            Two-factor authentication
          </DialogTitle>
          <DialogDescription>
            Add a second factor to your sign-in. You'll enter a 6-digit code
            from an authenticator app (Google Authenticator, Authy, 1Password,
            Bitwarden) after your password.
          </DialogDescription>
        </DialogHeader>

        {/* ── Idle: enrolled or not ─────────────────────────────────── */}
        {phase === "idle" && status && (
          <div className="space-y-4" data-testid="mfa-idle-panel">
            {status.mfa_enrolled ? (
              <>
                <div className="flex items-start gap-3 rounded-sm border border-emerald-900/60 bg-emerald-950/30 p-3">
                  <ShieldCheck className="h-5 w-5 text-emerald-400 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-emerald-200"
                         data-testid="mfa-status-enabled">MFA is enabled</div>
                    <div className="text-xs text-emerald-100/70 mt-0.5">
                      Backup codes remaining:{" "}
                      <span data-testid="mfa-backup-remaining"
                            className="font-mono">{status.backup_codes_remaining}</span>
                    </div>
                  </div>
                </div>
                <form onSubmit={disable} className="space-y-2">
                  <Label className="label-tag" htmlFor="mfa-disable-code">
                    To disable, enter a current code
                  </Label>
                  <Input id="mfa-disable-code"
                    data-testid="mfa-disable-code-input"
                    value={code} onChange={(e) => setCode(e.target.value)}
                    placeholder="123456 or XXXX-XXXX" required
                    className="bg-secondary border-zinc-800 rounded-sm tracking-widest" />
                  <Button type="submit"
                    data-testid="mfa-disable-submit-btn"
                    disabled={busy || !code}
                    variant="destructive"
                    className="w-full rounded-sm">
                    {busy
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <><ShieldOff className="h-4 w-4 mr-2" /> Disable MFA</>}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <div className="text-sm text-zinc-400">
                  MFA is not enabled on your account. Setting it up takes about
                  a minute — you'll scan a QR code with your authenticator and
                  confirm one code.
                </div>
                <Button onClick={startEnroll}
                  data-testid="mfa-start-enroll-btn"
                  disabled={busy}
                  className="w-full bg-emerald-700 hover:bg-emerald-600 rounded-sm">
                  {busy
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : "Set up MFA"}
                </Button>
              </>
            )}
          </div>
        )}

        {/* ── Enrolling ────────────────────────────────────────────── */}
        {phase === "enrolling" && enrollData && (
          <div className="space-y-3" data-testid="mfa-enrolling-panel">
            <div className="flex justify-center">
              <img src={enrollData.qr_png_data_url}
                alt="Scan with your authenticator app"
                data-testid="mfa-qr-img"
                className="w-40 h-40 rounded-sm border border-zinc-800 bg-white p-2" />
            </div>
            <div className="text-xs text-zinc-500">
              Can't scan? Enter this key manually:
            </div>
            <div className="rounded-sm border border-zinc-800 bg-black/40 p-2
                            font-mono text-xs break-all select-all"
                 data-testid="mfa-manual-key">
              {enrollData.secret}
            </div>
            <form onSubmit={confirmEnroll} className="space-y-2 pt-2">
              <Label className="label-tag" htmlFor="mfa-confirm-code">
                Enter the 6-digit code from your app to confirm
              </Label>
              <Input id="mfa-confirm-code"
                data-testid="mfa-confirm-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456" required autoFocus
                inputMode="numeric" maxLength={6}
                className="bg-secondary border-zinc-800 rounded-sm tracking-widest" />
              <Button type="submit"
                data-testid="mfa-confirm-submit-btn"
                disabled={busy || code.length < 6}
                className="w-full bg-emerald-700 hover:bg-emerald-600 rounded-sm">
                {busy
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : "Confirm & enable"}
              </Button>
            </form>
          </div>
        )}

        {/* ── Backup codes (shown once) ────────────────────────────── */}
        {phase === "backup" && finalCodes && (
          <div className="space-y-3" data-testid="mfa-backup-panel">
            <div className="rounded-sm border border-amber-900/60 bg-amber-950/20 p-3
                            text-xs text-amber-200/90">
              Save these 10 one-time backup codes somewhere safe (password
              manager, printout). Each one works exactly once if you lose
              access to your authenticator. <strong>They will not be shown again.</strong>
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-sm border border-zinc-800
                            bg-black/40 p-3 font-mono text-sm"
                 data-testid="mfa-backup-codes-grid">
              {finalCodes.map((c) => (
                <div key={c} className="select-all">{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={copyBackup} variant="outline"
                data-testid="mfa-backup-copy-btn"
                className="rounded-sm">
                {copied
                  ? <><Check className="h-4 w-4 mr-1.5" /> Copied</>
                  : <><Copy className="h-4 w-4 mr-1.5" /> Copy all</>}
              </Button>
              <div className="flex-1" />
              <label className="flex items-center gap-2 text-xs text-zinc-400">
                <input type="checkbox"
                  data-testid="mfa-backup-saved-checkbox"
                  checked={savedBackup}
                  onChange={(e) => setSavedBackup(e.target.checked)} />
                I've saved these codes
              </label>
            </div>
            <DialogFooter>
              <Button onClick={dismissBackup}
                data-testid="mfa-backup-done-btn"
                disabled={!savedBackup}
                className="w-full bg-emerald-700 hover:bg-emerald-600 rounded-sm">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MfaSettingsDialog;
